<template>
  <div>
    <AppCard
      class="dash-provider"
      header-color="bg-grey-11"
      :title="$t(form ? addTitle : title)"
      bordered
    >
      <q-form
        v-if="form"
        ref="form"
        class="q-pa-lg"
      >
        <div class="border-bottom q-pb-lg fields q-gutter-y-md">
          <AppInput
            :value="provider.label"
            :label="$t('label.type')"
            readonly
            disabled
          >
            <template #prepend>
              <q-img
                :src="require(`common/assets/${form.type}.svg`)"
                class="q-mr-sm"
                width="16px"
                height="16px"
              />
            </template>
          </AppInput>
          <AppInput
            v-for="field in fields"
            :key="field.name"
            v-model="providerFields[field.name]"
            v-bind="field.options"
            :label="$t(field.titleKey)"
            :readonly="isReadonlyField(field.name)"
            :disabled="isReadonlyField(field.name)"
            hide-bottom-space
            lazy-rules
          >
            <template
              v-if="field.options.hint"
              #hint
            >
              <div v-html="field.options.hint"/>
            </template>
          </AppInput>
        </div>
        <div class="q-pt-lg justify-between row">
          <AppButton
            class="col-3"
            color="white"
            text-color="grey"
            :label="$t('button.cancel')"
            outline
            @click="onCancel"
          />
          <AppButton
            class="col-8"
            text-color="black"
            :label="isExistProvider ? $t('button.save') : addButtonLabel"
            :loading="loading"
            @click="addIntegration"
          />
        </div>
      </q-form>
      <template v-else>
        <div
          v-for="item in value"
          :key="item.id"
          class="border-bottom row q-pa-lg"
        >
          <div class="row items-center col-7 no-wrap">
            <div class="q-pa-xs">
              <q-img
                :src="require(`common/assets/${item.type}.svg`)"
                width="20px"
                height="20px"
              />
            </div>
            <div class="q-pl-sm text-15-700">{{ item.title }}</div>
          </div>
          <div class="row items-center col-5 justify-end">
            <div class="q-pr-md text-grey">{{ getLabelByValue(item.type) }}</div>
            <q-icon
              name="fa-solid fa-ellipsis-vertical"
              class="cursor-pointer"
              size="13px"
            >
              <ContextMenu>
                <ContextMenuButton
                  :label="$t('button.edit')"
                  icon-left="fl:set"
                  @click="showProviderForm(item)"
                />
                <ContextMenuButton
                  :label="$t('button.delete')"
                  icon-left="fl:trash"
                  @click="$emit('remove', item.id)"
                />
              </ContextMenu>
            </q-icon>
          </div>
        </div>
        <div class="text-center q-pa-lg">
          <AppButtonLink :label="addProviderButtonLabel">
            <ContextMenu>
              <ContextMenuButton
                v-for="item in options"
                :key="item.value"
                v-close-popup
                :label="item.label"
                @click="showProviderForm({ type: item.value })"
              />
            </ContextMenu>
          </AppButtonLink>
        </div>
      </template>
    </AppCard>

    <AttentionDialog
      :is-open="form && isShowLeavePageDialog"
      :title="$t('title.attention')"
      img="attention-list.svg"
      @close="isShowLeavePageDialog = false"
    >
      <div class="row justify-center">
        <div class="col-8">
          <div class="text-19-400 text-center q-py-lg q-gutter-y-lg">
            <div v-if="form && isShowLeavePageDialog">{{ $t('text.youNeedToSave', { name: provider.label }) }}</div>
          </div>
        </div>
      </div>

      <template #footer>
        <AppButton
          v-close-popup
          class="col-4"
          :label="$t('button.back')"
        />
        <AppButton
          class="col"
          text-color="black"
          icon-right="fl:ring_bold"
          :label="$t('button.save')"
          :loading="loading"
          @click="addIntegration"
        />
      </template>
    </AttentionDialog>
  </div>
</template>

<script>
import AppCard from 'common/components/AppCard';
import AppInput from 'common/components/AppInput';
import AppButton from 'common/components/buttons/AppButton';
import AppButtonLink from 'common/components/buttons/AppButtonLink';
import ContextMenu from 'common/components/contextMenu/ContextMenu';
import ContextMenuButton from 'common/components/contextMenu/ContextMenuButton';
import AttentionDialog from 'common/components/dialogs/AttentionDialog';
import { INVALID_AUTORESPONDER_PROVIDER_API_KEY, INVALID_WEBINAR_PROVIDER_API_KEY } from 'common/enums/errorCodes';
import handleError from 'common/helpers/handleError';
import { notifyError } from 'common/helpers/notify';
import { mapState } from 'vuex';

export default {
  name: 'ProviderList',
  components: {
    AttentionDialog,
    ContextMenuButton,
    ContextMenu,
    AppInput,
    AppButton,
    AppButtonLink,
    AppCard,
  },
  props: {
    options: {
      type: Array,
      required: true,
    },
    value: {
      type: Array,
      default: () => ([]),
    },
    addButtonLabel: {
      type: String,
      required: true,
    },
    addProviderButtonLabel: {
      type: String,
      required: true,
    },
    addTitle: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    createFn: {
      type: Function,
      required: true,
    },
    updateFn: {
      type: Function,
      required: true,
    },
  },
  emits: ['remove'],
  data() {
    return {
      form: undefined,
      providerFields: {},
      loading: false,
      isShowLeavePageDialog: false,
    };
  },
  computed: {
    ...mapState('app', ['layout']),
    provider() {
      if (this.form) {
        return this.options.find((item) => item.value.toLowerCase() === this.form.type.toLowerCase());
      }
      return undefined;
    },
    fields() {
      if (this.form) {
        return this.provider.fields
          .filter((field) => !(
            !this.form.id
            && this.isZoom
            && field.name === 'accountName'));
      }
      return [];
    },
    isExistProvider() {
      return this.form && this.form.id;
    },
    isExistZoom() {
      return this.isExistProvider && this.isZoom;
    },
    isZoom() {
      return this.form && this.form.type === ENUMS.WEBINAR_TYPES.ZOOM;
    },
  },
  watch: {
    form: {
      handler(value) {
        this.$emit('is-provider-editing', !!value);
      },
    },
  },
  methods: {
    showLeavePageDialog() {
      this.isShowLeavePageDialog = true;
    },
    getLabelByValue(value) {
      return this.options.find((option) => option.value.toLowerCase() === value.toLowerCase()).label;
    },
    async addIntegration() {
      const isValid = await this.$refs.form.validate();
      if (!isValid) {
        return;
      }
      if (!this.form.id && this.isZoom) {
        // eslint-disable-next-line no-restricted-globals
        location.href = `${this.layout.zoomOauthLink}&state=title=${this.providerFields.title};from=${this.$route.fullPath};`;
        return;
      }
      try {
        this.loading = true;
        const fields = { ...this.providerFields };
        const title = this.providerFields.title;
        delete fields.title;

        if (this.isExistZoom) {
          delete fields.accountName;
        }

        const body = {
          title,
          meta: {
            ...fields,
            type: this.form.type.toLowerCase(),
          },
        };

        if (this.form.id) {
          await this.updateFn({
            id: this.form.id,
            body,
          });
        } else {
          await this.createFn(body);
        }
        this.onCancel();
      } catch (error) {
        const code = error.response?.data?.code;
        if ([INVALID_AUTORESPONDER_PROVIDER_API_KEY, INVALID_WEBINAR_PROVIDER_API_KEY].includes(code)) {
          notifyError(this.$t('notification.integrationFailed'));
        } else {
          handleError(error);
        }
      } finally {
        this.loading = false;
      }
    },
    showProviderForm(item) {
      this.form = { ...item };
      if (this.form.id) {
        Object.keys(this.form.meta)
          .forEach((key) => this.$set(this.providerFields, key, this.form.meta[key]));
        this.$set(this.providerFields, 'title', this.form.title);
      } else {
        this.$set(this.providerFields, 'title', this.provider.label);
      }
    },
    isReadonlyField(fieldName) {
      return this.isExistZoom && fieldName === 'accountName';
    },
    onCancel() {
      this.form = undefined;
      this.providerFields = {};
    },
  },
};
</script>

<style scoped lang="scss">
.dash-provider {
  width: $form-width;
}
</style>
